import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import type { EventMaster, EventResultFormat } from './useEventManagement';

/**
 * イベント一覧 (マスタ管理) ページのデータ層フック。
 *
 * get_event_list でイベント一覧 (申込サマリー付き) を取得し、
 * upsert_event で mst_event の新規登録・編集を行う。
 * event_code は申込サイト CGI と連動する運用キーのため新規登録時のみ指定できる
 * (サーバー側でも UPDATE では変更しない)。
 */

// 一覧の1件 = イベントマスタ + 申込サマリー
export type EventListRow = EventMaster & {
  entry_count: number;
  attend_count: number;
  participant_count: number;
};

// 登録・編集ダイアログの送信値。id なしで新規 (event_code 必須)、id ありで更新
export type EventUpsertInput = {
  id?: number;
  event_code?: string;
  event_name: string;
  event_type: string | null;
  event_date: string | null;
  venue: string | null;
  fee_regular: number | null;
  fee_prepaid: number | null;
  entry_start: string | null;
  entry_end: string | null;
  is_active: boolean;
  chat_webhook_url: string | null;
  // 配置の呼び名 (空なら既定 卓 / 席)
  group_unit_label: string | null;
  position_unit_label: string | null;
  // 成績の形式 (null は成績を記録しない)。成績が登録済みのイベントはサーバー側で変更を拒否する
  result_format_code: string | null;
};

export function useEventList() {
  const [events, setEvents] = useState<EventListRow[]>([]);
  // 編集ダイアログの「成績の形式」の選択肢
  const [resultFormats, setResultFormats] = useState<EventResultFormat[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(ep('get_event_list'), {});
      if (res.data.status === 'success') {
        setEvents(res.data.data || []);
        setResultFormats(res.data.result_formats || []);
      } else {
        setError('イベント一覧の取得に失敗しました。');
      }
    } catch (e) {
      console.error('イベント一覧の取得エラー:', e);
      setError('イベント一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // 登録/編集を保存し、成功時は一覧を再取得する。失敗時はサーバーの理由を返す
  const saveEvent = useCallback(
    async (data: EventUpsertInput): Promise<{ ok: boolean; error?: string }> => {
      setSaving(true);
      try {
        const res = await axios.post(ep('upsert_event'), data);
        if (res.data.status === 'success') {
          await fetchEvents();
          return { ok: true };
        }
        return { ok: false, error: res.data.error || '保存に失敗しました。' };
      } catch (e: any) {
        // バリデーション不備 (400) はサーバーの理由メッセージをそのまま表示する
        console.error('イベントの保存エラー:', e);
        return { ok: false, error: e?.response?.data?.error || '保存に失敗しました。' };
      } finally {
        setSaving(false);
      }
    },
    [fetchEvents],
  );

  return { events, resultFormats, loading, saving, error, reload: fetchEvents, saveEvent };
}
